import type { LeadFlowAnswers, LeadFlowStepDefinition } from "../types";

import {
  getLeadFlowQuestionFieldNames,
  LEAD_FORM_CONTRACTS,
  POSTAL_CODE_FIELD_NAME,
} from "@homeinstead/forms/lead-form";
import { getSelectedCareTypes } from "./get-selected-care-types";

type UserSelection = Record<string, string | string[]>;

// The fields the contract allows in a URL are the non-personal ones, so they
// are also the only ones analytics may carry.
const NON_PERSONAL_FIELD_NAMES = new Set<string>(
  Object.keys(LEAD_FORM_CONTRACTS.careSeeker.queryPrefillParameters),
);

function getFieldSelection(
  fieldName: string,
  answers: LeadFlowAnswers,
): UserSelection {
  if (fieldName === "careType") {
    const careTypes = getSelectedCareTypes(answers);

    return careTypes.length > 0 ? { careType: careTypes } : {};
  }

  if (fieldName === POSTAL_CODE_FIELD_NAME) {
    return {
      ...(answers.postalCode ? { postalCode: answers.postalCode } : {}),
      ...(answers.organizationNumber
        ? { franchiseNumber: String(answers.organizationNumber) }
        : {}),
    };
  }

  const value = answers[fieldName as keyof LeadFlowAnswers];

  return typeof value === "string" && value !== ""
    ? { [fieldName]: value }
    : {};
}

/**
 * The `user_selection` for a step's `Lead Form Step` event: the contract
 * values of the non-personal questions it asks. The postal question adds the
 * office the visitor picked. Contact fields never appear.
 */
function getStepUserSelection(
  step: LeadFlowStepDefinition,
  answers: LeadFlowAnswers,
): UserSelection {
  if (step._type !== "leadFlowStep") {
    return {};
  }

  return Object.assign(
    {},
    ...(step.items ?? [])
      .flatMap((item) => getLeadFlowQuestionFieldNames(item))
      .filter((fieldName) => NON_PERSONAL_FIELD_NAMES.has(fieldName))
      .map((fieldName) => getFieldSelection(fieldName, answers)),
  );
}

export { getStepUserSelection };
